import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

// import { TimeRangePickerWithInput } from './time-range-picker-with-input';
import { ColumnConfig, ColumnFilter } from './types';

import {
  Button,
  Checkbox,
  Dropdown,
  Icon,
  TextInput,
  TimeRangePickerWithInput,
} from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';

const FilterSection = styled.div`
  padding: 8px 12px;
  box-sizing: border-box;
  overflow: hidden;
`;

const FilterInput = styled(TextInput)`
  width: 100%;
  max-width: 100%;
`;

const FilterSelect = styled.select`
  width: 100%;
  max-width: 100%;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.dataTable.dropdownBorderColor};
  border-radius: 4px;
  font-size: 12px;
  background-color: ${({ theme }) => theme.dataTable.backgroundColor};
  color: ${({ theme }) => theme.dataTable.rowTextColor};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.dataTable.sortIconActiveColor};
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid ${({ theme }) => theme.dataTable.dropdownBorderColor};
`;

// Text filter component
const TextFilter: React.FC<{
  column: ColumnConfig;
  filter: ColumnFilter | null;
  onFilterChange: (filter: ColumnFilter | null) => void;
  onApply?: () => void;
}> = ({ column, filter, onFilterChange, onApply }) => {
  const [operator, setOperator] = useState<string>(filter?.operator || 'contains');
  const [value, setValue] = useState(() => {
    const initialValue = filter?.values?.[0];
    return typeof initialValue === 'string' ? initialValue : '';
  });

  const handleApply = useCallback(() => {
    if (value.trim()) {
      onFilterChange({
        columnKey: column.key,
        operator: operator as any,
        values: [value.trim()],
        logic: 'AND',
      });
    } else {
      onFilterChange(null);
    }
    onApply?.();
  }, [column.key, operator, value, onFilterChange, onApply]);

  const handleClear = useCallback(() => {
    setValue('');
    onFilterChange(null);
  }, [onFilterChange]);

  return (
    <>
      <FilterSection>
        <FilterSelect value={operator} onChange={(e) => setOperator(e.target.value)}>
          <option value="contains">Contains</option>
          <option value="not_contains">Does not contain</option>
          <option value="equals">Equals</option>
          <option value="not_equals">Does not equal</option>
          <option value="starts_with">Starts with</option>
          <option value="ends_with">Ends with</option>
          <option value="is_empty">Is empty</option>
          <option value="not_empty">Is not empty</option>
        </FilterSelect>
      </FilterSection>
      {!['is_empty', 'not_empty'].includes(operator) && (
        <FilterSection>
          <FilterInput
            value={value}
            onChange={(e) => setValue((e.target as HTMLInputElement).value)}
            placeholder="Enter value..."
            onKeyPress={(e) => e.key === 'Enter' && handleApply()}
          />
        </FilterSection>
      )}
      <FilterActions>
        <Button buttonType="primary" buttonSize="small" onClick={handleApply}>
          Apply
        </Button>
        <Button buttonType="secondary" buttonSize="small" onClick={handleClear}>
          Clear
        </Button>
      </FilterActions>
    </>
  );
};

// Date filter component
const DateFilter: React.FC<{
  timezone?: string;
  timeFormat?: '12h' | '24h';
  column: ColumnConfig;
  filter: ColumnFilter | null;
  onFilterChange: (filter: ColumnFilter | null) => void;
  onDatePickerOpenChange?: (isOpen: boolean) => void;
  onApply?: () => void;
}> = ({
  timezone,
  timeFormat,
  column,
  filter,
  onFilterChange,
  onDatePickerOpenChange,
  onApply,
}) => {
  const [operator, setOperator] = useState<string>(filter?.operator || 'on');
  const [value, setValue] = useState<string>(
    filter?.values?.[0] ||
      (column.includeTime ? dayjs().utc().toISOString() : dayjs().utc().format('YYYY-MM-DD')),
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const handleApply = useCallback(() => {
    if (!value.trim()) {
      onFilterChange(null);
      onApply?.();
      return;
    }

    onFilterChange({
      columnKey: column.key,
      operator: operator as any,
      values: [value],
      logic: 'AND',
    });
    onApply?.();
  }, [value, onFilterChange, column.key, operator, onApply]);

  const handleClear = useCallback(() => {
    setValue('');
    setOperator('on');
    onFilterChange(null);
  }, [onFilterChange]);

  const handleDatePickerOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsDatePickerOpen(isOpen);
      onDatePickerOpenChange?.(isOpen);
    },
    [onDatePickerOpenChange],
  );

  return (
    <>
      <FilterSection>
        <FilterSelect value={operator} onChange={(e) => setOperator(e.target.value)}>
          <option value="on">On</option>
          <option value="before">Before</option>
          <option value="after">After</option>
        </FilterSelect>
      </FilterSection>
      <FilterSection onClick={(e) => e.stopPropagation()}>
        <TimeRangePickerWithInput
          value={value}
          onChange={setValue}
          placeholder="Select date..."
          mode="single"
          calendarLayout="single"
          includeTime={column.includeTime ?? false}
          allowTimezoneChange={false}
          showTimezone={true}
          timeFormat={timeFormat ?? '24h'}
          plantTimezone={timezone}
          onOpenChange={handleDatePickerOpenChange}
        />
      </FilterSection>
      <FilterActions>
        <Button buttonType="primary" buttonSize="small" onClick={handleApply}>
          Apply
        </Button>
        <Button buttonType="secondary" buttonSize="small" onClick={handleClear}>
          Clear
        </Button>
      </FilterActions>
    </>
  );
};

// Number filter component
const NumberFilter: React.FC<{
  column: ColumnConfig;
  filter: ColumnFilter | null;
  onFilterChange: (filter: ColumnFilter | null) => void;
  onApply?: () => void;
}> = ({ column, filter, onFilterChange, onApply }) => {
  const [operator, setOperator] = useState<string>(filter?.operator || 'equals');
  const [value, setValue] = useState(() => {
    const initialValue = filter?.values?.[0];
    return initialValue !== null && initialValue !== undefined ? String(initialValue) : '';
  });
  const [error, setError] = useState<string>('');

  const handleApply = useCallback(() => {
    if (['is_empty', 'not_empty'].includes(operator)) {
      onFilterChange({
        columnKey: column.key,
        operator: operator as any,
        values: [],
        logic: 'AND',
      });
      onApply?.();
      return;
    }

    if (!value.trim()) {
      onFilterChange(null);
      onApply?.();
      return;
    }

    const numValue = Number(value.trim());
    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }

    setError('');
    onFilterChange({
      columnKey: column.key,
      operator: operator as any,
      values: [numValue],
      logic: 'AND',
    });
    onApply?.();
  }, [column.key, operator, value, onFilterChange, onApply]);

  const handleClear = useCallback(() => {
    setValue('');
    setError('');
    setOperator('equals');
    onFilterChange(null);
  }, [onFilterChange]);

  const handleValueChange = useCallback(
    (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = (e.target as HTMLInputElement).value;
      setValue(newValue);
      if (error && !isNaN(Number(newValue.trim()))) {
        setError('');
      }
    },
    [error],
  );

  return (
    <>
      <FilterSection>
        <FilterSelect value={operator} onChange={(e) => setOperator(e.target.value)}>
          <option value="equals">Equals</option>
          <option value="not_equals">Does not equal</option>
          <option value="greater_than">Greater than</option>
          <option value="less_than">Less than</option>
          <option value="greater_than_or_equal">Greater than or equal</option>
          <option value="less_than_or_equal">Less than or equal</option>
          <option value="is_empty">Is empty</option>
          <option value="not_empty">Is not empty</option>
        </FilterSelect>
      </FilterSection>
      {!['is_empty', 'not_empty'].includes(operator) && (
        <FilterSection>
          <FilterInput
            type="number"
            value={value}
            onChange={handleValueChange}
            placeholder="Enter number..."
            onKeyPress={(e) => e.key === 'Enter' && handleApply()}
          />
          {error && <div style={{ color: 'red', fontSize: '11px', marginTop: '4px' }}>{error}</div>}
        </FilterSection>
      )}
      <FilterActions>
        <Button buttonType="primary" buttonSize="small" onClick={handleApply}>
          Apply
        </Button>
        <Button buttonType="secondary" buttonSize="small" onClick={handleClear}>
          Clear
        </Button>
      </FilterActions>
    </>
  );
};

// Enum filter component
const EnumFilter: React.FC<{
  column: ColumnConfig;
  filter: ColumnFilter | null;
  onFilterChange: (filter: ColumnFilter | null) => void;
  uniqueValues: any[];
  onApply?: () => void;
}> = ({ column, filter, onFilterChange, uniqueValues, onApply }) => {
  const [selectedValues, setSelectedValues] = useState<Set<any>>(new Set(filter?.values || []));

  const handleToggleValue = useCallback((value: any) => {
    setSelectedValues((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return newSet;
    });
  }, []);

  const handleApply = useCallback(() => {
    if (selectedValues.size > 0) {
      onFilterChange({
        columnKey: column.key,
        operator: 'in',
        values: Array.from(selectedValues),
        logic: 'OR',
      });
    } else {
      onFilterChange(null);
    }
    onApply?.();
  }, [column.key, selectedValues, onFilterChange, onApply]);

  const handleClear = useCallback(() => {
    setSelectedValues(new Set());
    onFilterChange(null);
  }, [onFilterChange]);

  return (
    <>
      <FilterSection>
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {uniqueValues.map((value) => (
            <label
              key={value}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}
            >
              <Checkbox
                checked={selectedValues.has(value)}
                onChange={() => handleToggleValue(value)}
              />
              <span>{String(value)}</span>
            </label>
          ))}
        </div>
      </FilterSection>
      <FilterActions>
        <Button buttonType="primary" buttonSize="small" onClick={handleApply}>
          Apply
        </Button>
        <Button buttonType="secondary" buttonSize="small" onClick={handleClear}>
          Clear
        </Button>
      </FilterActions>
    </>
  );
};

// Main filter dropdown component
export const ColumnFilterDropdown: React.FC<{
  timezone?: string;
  timeFormat?: '12h' | '24h';
  column: ColumnConfig;
  filter: ColumnFilter | null;
  onFilterChange: (filter: ColumnFilter | null) => void;
  buttonRef: React.RefObject<HTMLDivElement>;
  data: any[];
  direction: 'right' | 'left';
}> = React.memo(
  ({ timezone, timeFormat, column, filter, onFilterChange, buttonRef, data, direction }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    // Detect column type and get unique values for enum
    const columnType = useMemo(() => {
      if (
        column.type === 'string' ||
        column.type === 'number' ||
        column.type === 'date' ||
        column.type === 'boolean'
      ) {
        return column.type;
      }
      // Custom type - check if it's an enum
      const allValues: string[] = [];
      let hasBlank = false;

      data.forEach((row) => {
        const value = row[column.key];
        if (value === null || value === undefined || value === '') {
          hasBlank = true;
        } else if (Array.isArray(value)) {
          const filtered = value.filter(Boolean);
          if (filtered.length === 0) {
            hasBlank = true;
          } else {
            allValues.push(...filtered);
          }
        } else {
          allValues.push(value);
        }
      });

      const uniqueValues = Array.from(new Set(allValues));
      // Add 1 to count if there are blank values (for "Blank" option)
      const totalOptions = uniqueValues.length + (hasBlank ? 1 : 0);
      return totalOptions <= 10 ? 'enum' : 'string';
    }, [column.type, column.key, data]);

    const uniqueValues = useMemo(() => {
      if (columnType === 'enum') {
        const allValues: string[] = [];
        let hasBlank = false;

        data.forEach((row) => {
          const value = row[column.key];
          if (value === null || value === undefined || value === '') {
            hasBlank = true;
          } else if (Array.isArray(value)) {
            // If it's an array, add all values from the array
            const filtered = value.filter(Boolean);
            if (filtered.length === 0) {
              hasBlank = true;
            } else {
              allValues.push(...filtered);
            }
          } else {
            // If it's a single value, add it directly
            allValues.push(value);
          }
        });

        const unique = Array.from(new Set(allValues)).sort();
        // Add "Blank" at the beginning if there are blank values
        if (hasBlank) {
          unique.unshift('Blank');
        }
        return unique;
      }
      return [];
    }, [columnType, column.key, data]);

    return (
      <Dropdown.Root onClose={() => !isDatePickerOpen && setMenuOpen(false)} isOpen={menuOpen}>
        <Dropdown.Trigger
          ref={buttonRef}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          style={{
            position: 'absolute',
            bottom: '-5px',
            right: '-5px',
            padding: '0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            borderRadius: '2px',
            zIndex: 1,
          }}
        >
          <Icon name={ICONS.Filter} width="14" height="14" color={filter ? '#007bff' : '#737373'} />
        </Dropdown.Trigger>
        <Dropdown.Options
          position={direction}
          open={menuOpen}
          style={{
            zIndex: 1000,
            minWidth: '250px',
          }}
        >
          <Dropdown.Title>Filter by {column.label}</Dropdown.Title>
          {columnType === 'enum' ? (
            <EnumFilter
              key={`${column.key}-${JSON.stringify(filter)}`}
              column={column}
              filter={filter}
              onFilterChange={onFilterChange}
              uniqueValues={uniqueValues}
              onApply={() => setMenuOpen(false)}
            />
          ) : columnType === 'date' ? (
            <DateFilter
              key={`${column.key}-${JSON.stringify(filter)}`}
              column={column}
              filter={filter}
              onFilterChange={onFilterChange}
              timezone={timezone}
              timeFormat={timeFormat}
              onDatePickerOpenChange={setIsDatePickerOpen}
              onApply={() => setMenuOpen(false)}
            />
          ) : columnType === 'number' ? (
            <NumberFilter
              key={`${column.key}-${JSON.stringify(filter)}`}
              column={column}
              filter={filter}
              onFilterChange={onFilterChange}
              onApply={() => setMenuOpen(false)}
            />
          ) : (
            <TextFilter
              key={`${column.key}-${JSON.stringify(filter)}`}
              column={column}
              filter={filter}
              onFilterChange={onFilterChange}
              onApply={() => setMenuOpen(false)}
            />
          )}
        </Dropdown.Options>
      </Dropdown.Root>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent re-renders when only data changes
    // but preserve UI state like date picker month selection
    const propsEqual =
      prevProps.timezone === nextProps.timezone &&
      prevProps.timeFormat === nextProps.timeFormat &&
      prevProps.column.key === nextProps.column.key &&
      prevProps.column.type === nextProps.column.type &&
      prevProps.filter === nextProps.filter &&
      prevProps.direction === nextProps.direction;

    if (!propsEqual) return false;

    // For enum columns, check if unique values have changed significantly
    if (
      prevProps.column.type !== 'string' &&
      prevProps.column.type !== 'number' &&
      prevProps.column.type !== 'date' &&
      prevProps.column.type !== 'boolean'
    ) {
      // Extract unique values from both datasets
      const getUniqueValues = (data: any[]) => {
        const allValues: string[] = [];
        data.forEach((row) => {
          const value = row[prevProps.column.key];
          if (value) {
            if (Array.isArray(value)) {
              allValues.push(...value.filter(Boolean));
            } else {
              allValues.push(value);
            }
          }
        });
        return Array.from(new Set(allValues)).sort();
      };

      const prevUniqueValues = getUniqueValues(prevProps.data);
      const nextUniqueValues = getUniqueValues(nextProps.data);

      // Only re-render if unique values have changed
      return JSON.stringify(prevUniqueValues) === JSON.stringify(nextUniqueValues);
    }

    return true;
  },
);
